import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Stack,
  Paper,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  Divider,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SendIcon from '@mui/icons-material/Send';
import { useMe } from '../api/hooks.js';
import { api } from '../api/client.js';
import { useT } from '../i18n/useT.js';

type EmailProvider = 'smtp' | 'ses';

interface EmailSettings {
  enabled: boolean;
  provider: EmailProvider;
  from: string;
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPassSet: boolean;
}

function errMsg(e: unknown): string {
  return (
    (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
    'Request failed'
  );
}

export function AdminEmailPage() {
  const all = useT();
  const t = all.admin.email;
  const needSuperAdmin = all.admin.needSuperAdmin;
  const { data: me } = useMe();
  const qc = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);

  const settings = useQuery({
    enabled: !!me?.isSuperAdmin,
    queryKey: ['admin-email-settings'],
    queryFn: async () => (await api.get('/v1/admin/email-settings')).data as EmailSettings,
  });

  // Local form state, seeded from the server once loaded.
  const [form, setForm] = useState<EmailSettings>({
    enabled: false,
    provider: 'ses',
    from: '',
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPassSet: false,
  });
  const [password, setPassword] = useState('');
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    if (settings.data) setForm(settings.data);
  }, [settings.data]);
  useEffect(() => {
    if (me?.email && !testTo) setTestTo(me.email);
  }, [me?.email, testTo]);

  const save = useMutation({
    mutationFn: async () =>
      api.put('/v1/admin/email-settings', {
        enabled: form.enabled,
        provider: form.provider,
        from: form.from,
        smtpHost: form.smtpHost,
        smtpPort: Number(form.smtpPort),
        smtpUser: form.smtpUser,
        // Only send the password when the admin typed a new one (write-only).
        ...(password ? { smtpPass: password } : {}),
      }),
    onSuccess: () => {
      setPassword('');
      qc.invalidateQueries({ queryKey: ['admin-email-settings'] });
      setToast('Email settings saved');
    },
    onError: (e) => setToast(errMsg(e)),
  });

  const sendTest = useMutation({
    mutationFn: async () => api.post('/v1/admin/email-settings/test', { to: testTo }),
    onSuccess: () => setToast(`Test email sent to ${testTo}`),
    onError: (e) => setToast(errMsg(e)),
  });

  if (me && !me.isSuperAdmin) {
    return <Alert severity="error">{needSuperAdmin}</Alert>;
  }

  const set = <K extends keyof EmailSettings>(k: K, v: EmailSettings[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Box sx={{ maxWidth: 680 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <MailOutlineIcon color="primary" />
        <Typography variant="h5">{t.title}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t.subtitle}
      </Typography>

      <Paper sx={{ p: 3 }}>
        <FormControlLabel
          control={<Switch checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />}
          label={t.sendEmail}
        />

        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Delivery method
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={form.provider}
            onChange={(_e, v) => v && set('provider', v as EmailProvider)}
          >
            <ToggleButton value="ses">{t.ses}</ToggleButton>
            <ToggleButton value="smtp">SMTP server</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField
            label={t.fromAddress}
            size="small"
            fullWidth
            placeholder={t.fromPlaceholder}
            value={form.from}
            onChange={(e) => set('from', e.target.value)}
            helperText={
              form.provider === 'ses'
                ? 'Must be an address on a domain/identity verified in Amazon SES.'
                : undefined
            }
          />

          {form.provider === 'ses' ? (
            <Alert severity="info" variant="outlined">
              Amazon SES sends using this server's AWS IAM identity. No SMTP credentials needed. Make
              sure the sending domain is verified in SES. While your SES account is in the sandbox, you
              can only send to verified recipient addresses.
            </Alert>
          ) : (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label={t.smtpHost}
                  size="small"
                  fullWidth
                  placeholder="smtp.gmail.com"
                  value={form.smtpHost}
                  onChange={(e) => set('smtpHost', e.target.value)}
                />
                <TextField
                  label={t.port}
                  size="small"
                  type="number"
                  sx={{ width: { sm: 120 } }}
                  value={form.smtpPort}
                  onChange={(e) => set('smtpPort', Number(e.target.value))}
                  helperText={t.portHelp}
                />
              </Stack>
              <TextField
                label={t.smtpUser}
                size="small"
                fullWidth
                placeholder="you@gmail.com"
                value={form.smtpUser}
                onChange={(e) => set('smtpUser', e.target.value)}
              />
              <TextField
                label={t.smtpPassword}
                size="small"
                fullWidth
                type="password"
                placeholder={form.smtpPassSet ? '•••••••• (unchanged)' : 'App password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                helperText={
                  form.smtpPassSet
                    ? 'A password is saved. Leave blank to keep it, or type a new one to replace it.'
                    : 'For Gmail, create an App Password (Account → Security → 2-Step → App passwords).'
                }
                InputProps={{
                  endAdornment: form.smtpPassSet ? (
                    <InputAdornment position="end">
                      <Typography variant="caption" color="success.main">
                        saved
                      </Typography>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            </>
          )}
          <Box>
            <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
              {t.save}
            </Button>
          </Box>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t.testTitle}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t.testHint}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1.5 }} alignItems={{ sm: 'center' }}>
          <TextField
            label={t.recipient}
            size="small"
            type="email"
            sx={{ minWidth: 280 }}
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
          />
          <Button
            variant="outlined"
            startIcon={<SendIcon />}
            onClick={() => sendTest.mutate()}
            disabled={sendTest.isPending || !testTo}
          >
            Send test
          </Button>
        </Stack>
      </Paper>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
